
import React from "react";
import RegistrationGuard from "@/components/auth/RegistrationGuard";
import PsychologistProfileModal from "@/components/home/PsychologistProfileModal";
import PsychologistFilters from "@/components/home/PsychologistFilters";
import LoadingState from "@/components/schedule/LoadingState";
import ErrorState from "@/components/schedule/ErrorState";
import { useScheduleStep1 } from "@/hooks/useScheduleStep1";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, User, Calendar, Filter, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const ScheduleStep1 = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState("all");

  const {
    psychologists,
    isLoading,
    queryError,
    selectedPsychologistForModal,
    isProfileModalOpen,
    handleViewProfile,
    handleViewFullProfile,
    handleScheduleNow,
    closeModal,
  } = useScheduleStep1();

  // Filter psychologists based on search and filters
  const filteredPsychologists = useMemo(() => {
    return psychologists.filter(psychologist => {
      const matchesSearch = !searchQuery || 
        psychologist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (psychologist.specialties && psychologist.specialties.some((spec: string) => 
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        ));

      const matchesSpecialty = selectedSpecialties.length === 0 || 
        (psychologist.specialties && psychologist.specialties.some((spec: string) => 
          selectedSpecialties.includes(spec)
        ));

      const matchesPrice = priceRange === 'all' || 
        (priceRange === '0-100' && psychologist.price <= 100) ||
        (priceRange === '100-200' && psychologist.price > 100 && psychologist.price <= 200) ||
        (priceRange === '200-300' && psychologist.price > 200 && psychologist.price <= 300) ||
        (priceRange === '300+' && psychologist.price > 300);

      return matchesSearch && matchesSpecialty && matchesPrice;
    });
  }, [psychologists, searchQuery, selectedSpecialties, priceRange]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedSpecialties([]);
    setPriceRange("all");
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (queryError) {
    return <ErrorState />;
  }

  return (
    <RegistrationGuard>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Encontre o Psicólogo Ideal para Você
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Conecte-se com profissionais qualificados e inicie sua jornada de bem-estar mental
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="flex items-center gap-2 text-blue-100">
                  <User className="h-5 w-5" />
                  <span>{psychologists.length}+ psicólogos disponíveis</span>
                </div>
                <div className="flex items-center gap-2 text-blue-100">
                  <Calendar className="h-5 w-5" />
                  <span>Agendamento online</span>
                </div>
                <div className="flex items-center gap-2 text-blue-100">
                  <Star className="h-5 w-5" />
                  <span>Profissionais verificados</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          {/* Filters Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtros de Busca
              </CardTitle>
              <CardDescription>
                Use os filtros abaixo para encontrar o psicólogo ideal para suas necessidades
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PsychologistFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedSpecialties={selectedSpecialties}
                onSpecialtiesChange={setSelectedSpecialties}
                priceRange={priceRange}
                onPriceRangeChange={setPriceRange}
                onClearFilters={handleClearFilters}
              />
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Psicólogos Disponíveis
              </h2>
              <p className="text-gray-600">
                {filteredPsychologists.length} profissionais encontrados
              </p>
            </div>
          </div>

          {/* Psychologists Grid */}
          {filteredPsychologists.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum psicólogo encontrado
                </h3>
                <p className="text-gray-600 mb-4">
                  Tente ajustar os filtros ou buscar por outros termos
                </p>
                <Button onClick={handleClearFilters} variant="outline">
                  Limpar Filtros
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPsychologists.map((psychologist) => (
                <Card key={psychologist.id} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100">
                        {psychologist.photoUrl ? (
                          <img 
                            src={psychologist.photoUrl} 
                            alt={psychologist.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {psychologist.name}
                        </h3>
                        <p className="text-sm text-gray-500">CRP: {psychologist.crp}</p>
                        <div className="flex items-center mt-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="text-sm ml-1">{psychologist.rating?.toFixed(1) || '5.0'}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {psychologist.shortBio || psychologist.bio}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {psychologist.specialties?.slice(0, 3).map((specialty, index) => (
                        <Badge key={index} className="bg-green-500 hover:bg-green-600 text-white text-xs">
                          {specialty}
                        </Badge>
                      ))}
                      {psychologist.specialties && psychologist.specialties.length > 3 && (
                        <Badge className="bg-green-500 hover:bg-green-600 text-white text-xs">
                          +{psychologist.specialties.length - 3}
                        </Badge>
                      )}
                    </div>

                    {psychologist.languages && psychologist.languages.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {psychologist.languages.slice(0, 2).map((language, index) => (
                          <Badge key={index} className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                            {language}
                          </Badge>
                        ))}
                        {psychologist.languages.length > 2 && (
                          <Badge className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                            +{psychologist.languages.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <span className="text-2xl font-bold text-primary">
                          R$ {psychologist.price?.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-sm text-gray-500 block">por consulta</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewProfile(psychologist.id)}
                        className="flex-1"
                      >
                        Ver Perfil
                      </Button>
                      <Button
                        onClick={() => handleScheduleNow(psychologist.id)}
                        size="sm"
                        className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                      >
                        Agendar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Profile Modal */}
        {selectedPsychologistForModal && (
          <PsychologistProfileModal
            isOpen={isProfileModalOpen}
            onClose={closeModal}
            psychologist={selectedPsychologistForModal}
            onBookConsultation={handleScheduleNow}
            onViewFullProfile={handleViewFullProfile}
          />
        )}
      </div>
    </RegistrationGuard>
  );
};

export default ScheduleStep1;
